import type { RogerDocUsageByClinic } from '../services/rogerDocApi';

export type UsageTimeRange = 'all_time' | '30_days' | '60_days';

/** Columns that can be sorted on the clinician usage table. */
export type UsageSortColumn =
  | 'full_name'
  | 'title'
  | 'total_visits'
  | 'oasis_visits'
  | 'routine_visits'
  | 'oasis_savings'
  | 'routine_savings';

export type UsageSortDirection = 'asc' | 'desc';

export type UsageSortConfig = {
  column: UsageSortColumn;
  direction: UsageSortDirection;
};

/** Preserves historical ordering: Total Visits descending for the selected range. */
export const DEFAULT_USAGE_SORT: UsageSortConfig = {
  column: 'total_visits',
  direction: 'desc',
};

export const USAGE_SORT_LABELS: Record<UsageSortColumn, string> = {
  full_name: 'Full name',
  title: 'Title',
  total_visits: 'Total Visits',
  oasis_visits: 'Oasis Visits',
  routine_visits: 'Routine Visits',
  oasis_savings: 'OASIS Savings (min)',
  routine_savings: 'Routine Savings (min)',
};

const NUMERIC_SORT_COLUMNS = new Set<UsageSortColumn>([
  'total_visits',
  'oasis_visits',
  'routine_visits',
  'oasis_savings',
  'routine_savings',
]);

function fullName(user: RogerDocUsageByClinic): string {
  return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

function getNumericSortValue(
  user: RogerDocUsageByClinic,
  column: UsageSortColumn,
  timeRange: UsageTimeRange
): number {
  const range = user[timeRange];
  switch (column) {
    case 'total_visits':
      return range?.total_visits ?? 0;
    case 'oasis_visits':
      return range?.oasis_visits ?? 0;
    case 'routine_visits':
      return range?.routine_visits ?? 0;
    case 'oasis_savings':
      // Same parse as the cell renderer so sort order matches displayed values.
      return parseFloat((range?.avg_oasis_time_saving ?? 0).toString()) || 0;
    case 'routine_savings':
      return parseFloat((range?.avg_routine_time_saving ?? 0).toString()) || 0;
    default:
      return 0;
  }
}

export function compareUsageRows(
  a: RogerDocUsageByClinic,
  b: RogerDocUsageByClinic,
  sort: UsageSortConfig,
  timeRange: UsageTimeRange
): number {
  let result = 0;

  if (sort.column === 'full_name') {
    result = compareStrings(fullName(a), fullName(b));
  } else if (sort.column === 'title') {
    result = compareStrings(a.clinician_title ?? '', b.clinician_title ?? '');
  } else {
    result =
      getNumericSortValue(a, sort.column, timeRange) -
      getNumericSortValue(b, sort.column, timeRange);
  }

  if (result !== 0) {
    return sort.direction === 'asc' ? result : -result;
  }

  // Stable tie-breakers so pagination stays deterministic across re-renders.
  return (
    compareStrings(fullName(a), fullName(b)) ||
    compareStrings(a.username ?? '', b.username ?? '')
  );
}

/** Sort the full filtered set (call before slicing for pagination). */
export function sortUsageRows(
  rows: RogerDocUsageByClinic[],
  sort: UsageSortConfig,
  timeRange: UsageTimeRange
): RogerDocUsageByClinic[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const result = compareUsageRows(a.row, b.row, sort, timeRange);
      return result || a.index - b.index;
    })
    .map(({ row }) => row);
}

export function nextUsageSort(
  current: UsageSortConfig,
  column: UsageSortColumn
): UsageSortConfig {
  if (current.column === column) {
    return {
      column,
      direction: current.direction === 'asc' ? 'desc' : 'asc',
    };
  }
  return {
    column,
    // Numeric columns open descending (matches default Total Visits); text asc.
    direction: NUMERIC_SORT_COLUMNS.has(column) ? 'desc' : 'asc',
  };
}
